import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { wishlistAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { addressAPI } from '../api';
import toast from 'react-hot-toast';
import { Heart, User, MapPin, Lock, ArrowRight } from 'lucide-react';

export const WishlistPage = () => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['wishlist'],
    queryFn: () => wishlistAPI.get().then(r => r.data),
  });
  const ProductCard = require('../components/product/ProductCard').default;

  return (
    <div className="container" style={{ padding: '2rem 1.5rem' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>My Wishlist ({data?.wishlist?.length || 0} items)</h1>
      {isLoading ? <div style={{ textAlign: 'center', padding: '3rem' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        : data?.wishlist?.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '5rem' }}>
            <Heart size={48} color="#e5e7eb" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ color: '#9ca3af', marginBottom: 16 }}>Your wishlist is empty</h3>
            <Link to="/products" className="btn btn-primary">Browse Products</Link>
          </div>
        ) : (
          <div className="product-grid">
            {data.wishlist.map(w => <ProductCard key={w.id} product={{ ...w, id: w.product_id }} />)}
          </div>
        )}
    </div>
  );
};

export const ProfilePage = () => {
  const { user, fetchMe } = useAuth();
  const [tab, setTab] = useState('profile');
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '' });
  const [pwdForm, setPwdForm] = useState({ currentPassword: '', newPassword: '' });
  const qc = require('@tanstack/react-query').useQueryClient();

  const handleProfile = async (e) => {
    e.preventDefault();
    try {
      const { authAPI } = require('../api');
      await authAPI.updateProfile(form);
      await fetchMe();
      toast.success('Profile updated!');
    } catch { toast.error('Failed to update'); }
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    try {
      const { authAPI } = require('../api');
      await authAPI.changePassword(pwdForm);
      toast.success('Password changed!');
      setPwdForm({ currentPassword: '', newPassword: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div className="container" style={{ padding: '2rem 1.5rem', maxWidth: 800 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>My Account</h1>
      <div style={{ display: 'flex', gap: 24 }}>
        <div style={{ width: 200 }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, #f43397, #6c3fc5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, color: 'white', margin: '0 auto 12px' }}>
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <p style={{ fontWeight: 600 }}>{user?.name}</p>
            <p style={{ fontSize: 13, color: '#9ca3af' }}>{user?.email}</p>
          </div>
          {[{id:'profile',icon:<User size={16}/>,label:'Profile'},{id:'password',icon:<Lock size={16}/>,label:'Password'},{id:'addresses',icon:<MapPin size={16}/>,label:'Addresses'}].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: tab === t.id ? 600 : 400, background: tab === t.id ? '#fce4f1' : 'transparent', color: tab === t.id ? '#f43397' : '#374151', marginBottom: 4 }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }}>
          {tab === 'profile' && (
            <div className="card" style={{ padding: '1.5rem' }}>
              <h3 style={{ fontWeight: 700, marginBottom: 20 }}>Edit Profile</h3>
              <form onSubmit={handleProfile}>
                {[['name','Full Name',form.name],['phone','Phone Number',form.phone]].map(([f,l,v]) => (
                  <div key={f} className="form-group">
                    <label className="form-label">{l}</label>
                    <input className="form-input" value={v} onChange={e => setForm(fm => ({...fm, [f]: e.target.value}))} />
                  </div>
                ))}
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" value={user?.email} disabled style={{ background: '#f9f9f9' }} />
                </div>
                <button type="submit" className="btn btn-primary">Save Changes</button>
              </form>
            </div>
          )}
          {tab === 'password' && (
            <div className="card" style={{ padding: '1.5rem' }}>
              <h3 style={{ fontWeight: 700, marginBottom: 20 }}>Change Password</h3>
              <form onSubmit={handlePassword}>
                {[['currentPassword','Current Password'],['newPassword','New Password']].map(([f,l]) => (
                  <div key={f} className="form-group">
                    <label className="form-label">{l}</label>
                    <input type="password" className="form-input" value={pwdForm[f]} onChange={e => setPwdForm(fm => ({...fm, [f]: e.target.value}))} />
                  </div>
                ))}
                <button type="submit" className="btn btn-primary">Update Password</button>
              </form>
            </div>
          )}
          {tab === 'addresses' && <AddressesSection />}
        </div>
      </div>
    </div>
  );
};

const AddressesSection = () => {
  const { data, refetch } = useQuery({ queryKey: ['my-addresses'], queryFn: () => addressAPI.get().then(r => r.data) });
  const [form, setForm] = useState({ name: '', phone: '', street: '', city: '', state: '', pincode: '', is_default: false });
  const [show, setShow] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await addressAPI.add(form);
    await refetch();
    setShow(false);
    toast.success('Address added!');
  };

  return (
    <div className="card" style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <h3 style={{ fontWeight: 700 }}>My Addresses</h3>
        <button onClick={() => setShow(!show)} className="btn btn-primary btn-sm"><MapPin size={14} /> Add New</button>
      </div>
      {show && (
        <form onSubmit={handleSubmit} style={{ marginBottom: 20, padding: '16px', background: '#f9f9f9', borderRadius: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[['name','Name'],['phone','Phone'],['street','Street'],['city','City'],['state','State'],['pincode','Pincode']].map(([f,l]) => (
              <div key={f} style={{ gridColumn: f === 'street' ? '1/-1' : undefined }}>
                <label className="form-label">{l}</label>
                <input required className="form-input" value={form[f]} onChange={e => setForm(fm => ({...fm, [f]: e.target.value}))} />
              </div>
            ))}
          </div>
          <button type="submit" className="btn btn-primary btn-sm" style={{ marginTop: 12 }}>Save</button>
        </form>
      )}
      {data?.addresses?.map(addr => (
        <div key={addr.id} style={{ padding: '12px', border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <p style={{ fontWeight: 600 }}>{addr.name} • {addr.phone} {addr.is_default && <span className="badge badge-primary" style={{ marginLeft: 8, fontSize: 11 }}>Default</span>}</p>
            <button onClick={async () => { await addressAPI.delete(addr.id); refetch(); }} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>Delete</button>
          </div>
          <p style={{ color: '#6b7280', fontSize: 13 }}>{addr.street}, {addr.city}, {addr.state} - {addr.pincode}</p>
        </div>
      ))}
    </div>
  );
};

export const LoginPage = () => {
  const { login, isLoggedIn } = useAuth();
  const navigate = require('react-router-dom').useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  if (isLoggedIn) { navigate('/'); return null; }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(form.email, form.password);
    setLoading(false);
    if (result?.success) navigate(result.user.role === 'admin' ? '/admin' : '/');
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #fce4f1 0%, #e8e0ff 100%)' }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 1rem' }}>
        <div className="card" style={{ padding: '2.5rem' }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ width: 56, height: 56, background: 'linear-gradient(135deg, #f43397, #6c3fc5)', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: 24 }}>🛍️</div>
            <h2 style={{ fontSize: 24, fontWeight: 800 }}>Welcome Back</h2>
            <p style={{ color: '#9ca3af', fontSize: 14 }}>Sign in to your account</p>
          </div>
          <form onSubmit={handleSubmit}>
            {[['email','Email Address','email'],['password','Password','password']].map(([f,l,t]) => (
              <div key={f} className="form-group">
                <label className="form-label">{l}</label>
                <input type={t} required className="form-input" value={form[f]} onChange={e => setForm(fm => ({...fm, [f]: e.target.value}))} placeholder={f === 'email' ? 'you@example.com' : '••••••••'} />
              </div>
            ))}
            <div style={{ marginBottom: 16, display: 'flex', gap: 8, padding: '10px', background: '#f9f9f9', borderRadius: 8, fontSize: 13, color: '#6b7280' }}>
            </div>
            <button type="submit" disabled={loading} className="btn btn-primary btn-lg btn-full">
              {loading ? 'Signing in...' : 'Sign In'} <ArrowRight size={18} />
            </button>
          </form>
          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: '#9ca3af' }}>
            Don't have an account? <Link to="/register" style={{ color: '#f43397', fontWeight: 600 }}>Register</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const { register, isLoggedIn } = useAuth();
  const navigate = require('react-router-dom').useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', role: 'customer' });
  const [loading, setLoading] = useState(false);

  if (isLoggedIn) { navigate('/'); return null; }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await register(form);
    setLoading(false);
    if (result?.success) navigate('/');
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #fce4f1 0%, #e8e0ff 100%)', padding: '2rem 1rem' }}>
      <div style={{ width: '100%', maxWidth: 440 }}>
        <div className="card" style={{ padding: '2.5rem' }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ width: 56, height: 56, background: 'linear-gradient(135deg, #f43397, #6c3fc5)', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: 24 }}>✨</div>
            <h2 style={{ fontSize: 24, fontWeight: 800 }}>Create Account</h2>
            <p style={{ color: '#9ca3af', fontSize: 14 }}>Join millions of shoppers</p>
          </div>
          <form onSubmit={handleSubmit}>
            {[['name','Full Name','text'],['email','Email Address','email'],['phone','Phone Number','tel'],['password','Password','password']].map(([f,l,t]) => (
              <div key={f} className="form-group">
                <label className="form-label">{l}</label>
                <input type={t} required={f !== 'phone'} className="form-input" value={form[f]} onChange={e => setForm(fm => ({...fm, [f]: e.target.value}))} />
              </div>
            ))}
            <div className="form-group">
              <label className="form-label">Account Type</label>
              <select className="form-input" value={form.role} onChange={e => setForm(fm => ({...fm, role: e.target.value}))}>
                <option value="customer">Customer - I want to shop</option>
                <option value="seller">Seller - I want to sell</option>
              </select>
            </div>
            <button type="submit" disabled={loading} className="btn btn-primary btn-lg btn-full">
              {loading ? 'Creating Account...' : 'Create Account'} <ArrowRight size={18} />
            </button>
          </form>
          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: '#9ca3af' }}>
            Already have an account? <Link to="/login" style={{ color: '#f43397', fontWeight: 600 }}>Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
